import type { CategoryPrompt } from "../types";

import React from "react";

import {
  Cpu,
  Laptop,
  Network,
  Rocket,
  Smartphone,
  Star,
  Video,
} from "lucide-react";

export const techPrompts: CategoryPrompt[] = [
  {
    icon: <Smartphone />,
    title: "Interactive Product Feature Tour Page",
    description:
      "Build product pages with video demos, feature callouts with hotspots, and comparison tables showcasing specs.",
    prompt: `**Build an interactive product feature tour page with video demonstrations, clickable hotspots, and spec comparison tables.**

**PRODUCT TYPE:** e.g., "Laptop Features" OR "Smartphone Tour" OR "Smart Home Device" OR "Wireless Earbuds"

**FEATURE FOCUS:** e.g., Performance, design, connectivity, battery, camera, audio

**Visitor Type:** Choose one:
- [ ] Feature-focused (need specific capabilities)
- [ ] Visual learners (want to see in action)
- [ ] Spec comparers (evaluating options)

**Tone & Style:**
- Tech-savvy but accessible
- Feature-benefit focused
- Exciting innovation showcase
- "Experience the difference" energy

**CTA Guidelines:** Each CTA should:
- Reference key feature ("Get This Performance")
- Use innovation language ("Experience Innovation")
- Include spec highlight ("Shop 5G Speed")
- Emphasize capability ("Unlock Potential")

**Conversion Tactics Use:**
- Interactive product imagery with hotspots
- Video demonstrations of features
- Spec comparison tables
- Real-world use case scenarios
- Technical specs with plain-language explanations
- Expert reviews integrated

**Key Elements to Include:**
- **Hero Section:** Product image/video, headline feature, "Explore Features" CTA
- **Interactive Product View:** 360° product image with clickable hotspot annotations
- **Feature Hotspots (6-10):** Click reveals feature details, specs, benefits
- **Video Demos (3-5):** 30-90 sec videos showing key features in action
- **Feature Breakdown Sections:** 5-8 major features with visuals, specs, benefits, use cases
- **Comparison Table:** Your product vs. competitors (8-12 rows on key specs)
- **Technical Specifications:** Complete specs organized by category (processor, memory, display, etc.)
- **Real-World Performance:** Use case demonstrations with measurable results
- **Design Highlights:** Materials, build quality, aesthetic features
- **Compatibility & Connectivity:** What works with it, ecosystems, standards
- **Expert Reviews:** Tech reviewer quotes and ratings
- **Customer Use Cases:** How real users leverage features
- **Feature Priority Guide:** Which features matter for different user types
- **CTAs:** Hero, after feature sections, comparison table, footer (5-6 total)
- **FAQ:** 5-7 questions (Compatibility? Upgrade from previous? Warranty? Specs explained? Best for?)

**Goal:** Convert feature-conscious tech shoppers into confident buyers through interactive exploration that showcases innovation with video demonstrations, clickable hotspots that reveal detailed specs, and comparison tables proving superiority over alternatives.`,
  },
  {
    icon: <Video />,
    title: "Use-Case Landing Page",
    description:
      "Build a page targeting [a specific use case] with relevant features, performance specs, and testimonials from similar users.",
    prompt: `**Build a use-case specific landing page targeting particular user needs with relevant features, performance data, and testimonials from similar use scenarios.**

**USE CASE:** e.g., "Best for Video Editing" OR "Perfect for Gaming" OR "Ideal for Remote Work" OR "Built for Content Creation"

**PERFORMANCE PRIORITIES:** e.g., Processing power, graphics, battery life, portability, connectivity

**Target Customer:** Choose one:
- [ ] Use-specific (need for particular task)
- [ ] Performance-demanding (requirements-driven)
- [ ] Professional users (work depends on it)

**Tone & Style:**
- Use case expert advisor
- Performance-focused
- Workflow understanding
- "Built for this" confidence

**CTA Guidelines:** Each CTA should:
- Reference use case ("Perfect for Editing")
- Use performance language ("Power Your Work")
- Include capability benefit ("Handle Any Project")
- Emphasize suitability ("Made for This")

**Conversion Tactics Use:**
- Use-case specific benchmarks
- Workflow demonstrations
- Performance metrics for tasks
- Professional testimonials
- Software compatibility emphasis
- Real project examples

**Key Elements to Include:**
- **Hero Section:** Use case headline, user in scenario image, "Get Started" CTA
- **Why This Use Case Needs Specific Tech:** Requirements breakdown (2-3 paragraphs)
- **Key Requirements:** 5-7 must-have specs/features for this use case
- **Essential Features (6-10):** Feature name, why critical for use case, specs, real-world benefit
- **Performance Benchmarks:** Use-case relevant metrics (render times, FPS, export speeds, etc.)
- **Recommended Products:** 2-4 options ideal for use case with differentiation
- **Workflow Demonstrations:** Videos/screenshots showing product in use case action
- **Technical Specs Relevant to Use:** Processor, RAM, GPU, storage with use-case context
- **Software Compatibility:** Programs optimized for, certifications, ecosystem
- **Use Case Testimonials (8-12):** Name, profession/use, project types, performance praise, productivity gains
- **Real Project Examples:** Customer work created with product
- **Comparison Chart:** Use-case requirements vs. product capabilities (5-7 rows)
- **Performance vs. Competitors:** Benchmarks specific to use case
- **Productivity Impact:** Time saved, quality improvements, efficiency gains
- **Professional Endorsements:** Industry expert or influencer validation
- **Setup Guidance:** Configuration tips for optimal use-case performance
- **Accessories/Additions:** Complementary products for workflow
- **CTAs:** Hero, with products, after testimonials (5-6 total)
- **FAQ:** 6-8 questions (Handle my workload? Software compatible? Enough power? Upgrade path? Warranty?)

**Goal:** Convert use-case specific shoppers into confident buyers by demonstrating deep understanding of their workflow needs and proving through benchmarks, testimonials, and demonstrations that your product delivers the exact performance and features required for their particular professional or personal use case.`,
  },
  {
    icon: <Laptop />,
    title: "Pre-Order Launch Page",
    description:
      "Build a pre-order page with compelling imagery and key features.",
    prompt: `**Build a pre-order launch page that generates excitement for upcoming product release with compelling visuals and feature highlights.**

**PRODUCT LAUNCH:** e.g., "New Smartphone Pre-Order" OR "Next-Gen Laptop" OR "Wireless Earbuds V2"

**LAUNCH TIMING:** e.g., "Ships in 4 weeks" OR "Available October 15" OR "Q2 2026"

**Pre-Order Audience:** Choose one:
- [ ] Early adopters (want latest first)
- [ ] Tech enthusiasts (excited for innovation)
- [ ] Loyal customers (trust brand releases)

**Tone & Style:**
- Exciting and anticipation-building
- Innovation-focused
- Exclusive early access feel
- "Be first" energy

**CTA Guidelines:** Each CTA should:
- Emphasize exclusivity ("Pre-Order Now")
- Reference timing ("Be First to Own")
- Include incentive ("Save $X")
- Use scarcity ("Limited Quantities")

**Conversion Tactics Use:**
- Early-bird discount pricing
- Limited pre-order quantities
- Exclusive launch bonuses
- Ship date guarantee
- Reservation with minimal commitment
- Pre-order perks vs. launch day

**Key Elements to Include:**
- **Hero Section:** Product hero image/video, "Pre-Order Now" CTA, ship date, early-bird price
- **Launch Countdown:** Timer to ship date or pre-order close
- **Key Features Showcase:** 5-8 major innovations with visuals and specs
- **What's New:** Improvements over previous version
- **Pre-Order Benefits:** Early-bird price, exclusive color, bonus accessories, priority shipping
- **Tech Specs:** Complete specifications available at launch
- **High-Quality Visuals:** Multiple product angles, lifestyle shots, feature close-ups
- **Innovation Story:** What makes this generation special
- **Comparison:** New model vs. previous/competitors (3-5 rows)
- **Pre-Order Pricing:** Discount amount, savings vs. launch price
- **Ship Date Guarantee:** When it arrives, delivery commitment
- **Limited Availability:** Quantity or time-limited messaging
- **Reservation Details:** Payment timing (now vs. at ship), cancellation policy
- **Early Reviews:** Tech journalist previews, hands-on impressions
- **Video Showcase:** 2-3 minute feature highlights
- **Pre-Order Perks:** What early buyers get vs. waiting
- **CTAs:** Hero, after features, pricing section, footer (4-5 total)
- **FAQ:** 5-7 questions (When charged? Cancel how? Ship date? Quantity limits? Warranty starts?)

**Goal:** Maximize pre-order conversions through launch excitement, early-bird incentives, and exclusive perks that reward early commitment while clearly communicating ship dates and reservation terms to reduce purchase anxiety.`,
  },
  {
    icon: <Cpu />,
    title: "Influencer Endorsement Page",
    description:
      'Create a page featuring influencer reviews and recommendations, with their discount codes and "As Seen On" badges.',
    prompt: `**Build an influencer endorsement page featuring tech reviewer recommendations with discount codes and media validation badges.**

**INFLUENCER/CREATOR:** e.g., "@TechReviewerName" OR "YouTube Channel" OR "Multiple Tech Influencers"

**ENDORSEMENT TYPE:** e.g., Full review, hands-on demo, recommendation, award winner

**Audience Type:** Choose one:
- [ ] Influencer's followers (trust their reviews)
- [ ] Tech enthusiasts (research through reviewers)
- [ ] Validation seekers (want expert approval)

**Tone & Style:**
- Credible tech authority
- Unbiased review showcase
- Performance-proven confidence
- "Experts agree" validation

**CTA Guidelines:** Each CTA should:
- Reference reviewer ("Shop [Reviewer]'s Pick")
- Use endorsement language ("Expert Approved")
- Include discount code prominently
- Emphasize credibility ("Trusted by Tech Press")

**Conversion Tactics Use:**
- Multiple reviewer endorsements
- Video review embeds
- Exclusive discount codes
- Media outlet badges
- Award winner highlights
- Hands-on experience validation

**Key Elements to Include:**
- **Hero Section:** "As Reviewed By" headline, top reviewer quotes, "Shop Now" CTA
- **Featured Reviews (3-6):** Reviewer name/photo, outlet, rating/score, quote, video embed, full review link
- **Discount Codes:** Each reviewer's exclusive code with savings % and expiration
- **"As Seen On" Badges:** Tech publications, YouTube channels, podcasts (8-12 logos)
- **Video Reviews Embedded:** 3-5 full reviews or key excerpts
- **Review Highlights:** Pull quotes from multiple sources (10-15 quotes)
- **Ratings Compilation:** Scores from various outlets aggregated
- **Award Badges:** "Editor's Choice," "Best of Year," "Top Pick" displays
- **What Reviewers Love:** Common praise themes across reviews
- **Hands-On Photos:** Reviewers with product, unboxing, testing
- **Tech Press Coverage:** Articles, features, announcements
- **Comparison Reviews:** Where product ranked vs. competitors
- **Social Proof:** Follower counts of reviewers, view counts of videos
- **Community Reception:** Reddit, forums, social media sentiment
- **Verified Purchase Reviews:** Customer reviews echoing expert opinions
- **CTAs:** Hero, with discount codes, after reviews (5-6 total)
- **FAQ:** 3-4 questions (Codes stack? Influencer partnerships disclosed? Reviews paid? Where to watch?)

**Goal:** Convert review-influenced shoppers into buyers through credible tech expert endorsements, embedded video reviews, and exclusive discount codes that reward purchasing through trusted sources while building confidence through widespread media validation.`,
  },
  {
    icon: <Rocket />,
    title: "Spec Comparison Page",
    description:
      "Create a comparison page showing how my products compare to competitors on specs, price, and features.",
    prompt: `**Build a spec comparison page that positions your products as superior through side-by-side analysis of specifications, pricing, and features.**

**COMPARISON FOCUS:** e.g., "Us vs. Competition" OR "[Product] Compared" OR "Spec Showdown"

**COMPARISON ANGLE:** e.g., Performance specs, features, price-to-value, build quality

**Visitor Type:** Choose one:
- [ ] Active comparers (researching multiple brands)
- [ ] Spec-focused (need detailed comparisons)
- [ ] Value-conscious (best performance per dollar)

**Tone & Style:**
- Objective and data-driven
- Confident through facts
- Feature-benefit focused
- "Numbers don't lie" directness

**CTA Guidelines:** Each CTA should:
- Reference superiority ("Get Better Specs")
- Use value language ("Best Performance/Price")
- Include winner positioning ("Top-Rated Choice")
- Emphasize advantage ("More for Less")

**Conversion Tactics Use:**
- Detailed spec-by-spec comparison
- Price-per-performance calculations
- Feature checklist highlights
- Benchmark data
- Expert review scores
- Value proposition emphasis

**Key Elements to Include:**
- **Hero Section:** "See How We Compare" headline, "Shop the Winner" CTA
- **Comparison Table:** 15-25 rows (processor, RAM, storage, display, battery, camera, ports, weight, price, warranty)
- **Visual Comparison:** Side-by-side product images
- **Performance Benchmarks:** Real-world test results (speed, battery life, rendering times)
- **Feature Checklist:** What's included vs. missing in competitors
- **Price-to-Performance:** Value analysis showing cost per spec
- **Build Quality:** Materials, durability, design comparisons
- **Winner Highlights:** Where your product excels (badges or color coding)
- **Spec Explanations:** Plain-language breakdown of technical terms
- **Use-Case Performance:** How specs translate to real usage
- **Expert Review Scores:** Aggregated ratings across products
- **Customer Satisfaction:** Review ratings comparison
- **Upgrade Value:** What you get extra vs. competitors
- **Total Cost of Ownership:** Price plus accessories, warranties, longevity
- **CTAs:** Hero, after comparison table, value analysis (4-5 total)
- **FAQ:** 4-5 questions (Accurate specs? Updated when? Missing competitors? Biased? Source data?)

**Goal:** Convert spec-conscious shoppers into buyers through transparent, factual comparison that proves your product delivers superior specifications, more features, and better value than alternatives - backed by benchmarks and expert validation.`,
  },
  {
    icon: <Star />,
    title: "Warranty & Support Page",
    description:
      "Build a page explaining warranty program, support options, and customer success stories.",
    prompt: `**Build a warranty and support page that turns your service into a competitive advantage through transparent terms and customer success stories.**

**WARRANTY TYPE:** e.g., "2-Year Warranty" OR "Lifetime Support Promise" OR "Premium Care Program"

**COVERAGE SCOPE:** e.g., Hardware defects, accidental damage, software support, replacement policy

**Audience Type:** Choose one:
- [ ] Purchase-hesitant (need assurance)
- [ ] Long-term investors (want protection)
- [ ] Support-concerned (worried about issues)

**Tone & Style:**
- Reassuring and confident
- Customer-first approach
- Simple and transparent
- "We've got you covered" promise

**CTA Guidelines:** Each CTA should:
- Reference confidence ("Buy with Confidence")
- Use protection language ("Fully Protected")
- Include ease messaging ("Easy Support")
- Emphasize backing ("We Stand Behind It")

**Conversion Tactics Use:**
- Extended warranty emphasis
- 24/7 support availability
- Fast replacement turnaround
- Real support success stories
- Easy claim process demonstration
- Comparison to industry standards

**Key Elements to Include:**
- **Hero Section:** "Backed by Our Warranty & Support" headline, "Shop Protected" CTA
- **Warranty Coverage:** What's included (defects, damage, wear-and-tear specifics)
- **Warranty Duration:** Years/lifetime with clear start date
- **How Claims Work:** 3-4 simple steps (Contact → Diagnose → Replace/Repair → Resolved)
- **Support Options:** Phone, email, chat, video call with availability hours
- **Response Time Guarantees:** "24-hour response" or "Same-day support"
- **Replacement Process:** Turnaround time, shipping, advanced replacement option
- **What's NOT Covered:** Transparent exclusions (theft, intentional damage, etc.)
- **Customer Support Stories (5-8):** Name, issue, how resolved, timeline, satisfaction
- **Video Walkthrough:** Support process demonstration
- **Extended Warranty Options:** Upgrade to premium coverage details
- **International Support:** Global coverage if applicable
- **Comparison Chart:** Your warranty vs. competitors (4-6 rows)
- **Success Metrics:** "15,000+ claims resolved" or "4.9/5 support rating"
- **Expert Technicians:** Team credentials, training, experience
- **Self-Service Resources:** Knowledge base, tutorials, troubleshooting guides
- **Proactive Support:** Software updates, maintenance reminders
- **CTAs:** Hero, after stories, with products (4-5 total)
- **FAQ:** 6-8 questions (Registration needed? Proof of purchase? Transfer warranty? International? Extend coverage? Claim limit?)

**Goal:** Convert support-anxious shoppers into confident buyers by showcasing comprehensive warranty coverage, fast responsive support, and real customer success stories that prove your service backing eliminates purchase risk and ensures long-term satisfaction.`,
  },
  {
    icon: <Network />,
    title: "Gift Guide Landing Page",
    description:
      "Create a gift guide page organized by recipient and price point, featuring best-sellers with gift options.",
    prompt: `**Build a gift guide landing page organized by recipient type and price range to simplify tech gift shopping with best-seller highlights.**

**GIFT GUIDE FOCUS:** e.g., "Holiday Tech Gifts" OR "Back to School Guide" OR "Father's Day Tech"

**ORGANIZATION:** e.g., By recipient, by price, by tech category, by interest

**Shopper Type:** Choose one:
- [ ] Gift buyers (shopping for others)
- [ ] Budget-conscious (specific price range)
- [ ] Non-tech buyers (need guidance)

**Tone & Style:**
- Helpful gift consultant
- Occasion-appropriate excitement
- Tech-accessible for non-experts
- "Perfect gift guaranteed" confidence

**CTA Guidelines:** Each CTA should:
- Reference gifting ("Give the Perfect Gift")
- Use recipient language ("For Gamers")
- Include price clarity ("Gifts Under $100")
- Emphasize ease ("Shop Now")

**Conversion Tactics Use:**
- Price range filtering
- Recipient persona matching
- Best-seller badges
- Gift wrap/message options
- Bundle gift sets
- Last-minute/quick-ship highlights

**Key Elements to Include:**
- **Hero Section:** Gift guide headline, featured gift image, "Shop Gifts" CTA
- **Browse by Price:** Under $50, $50-$100, $100-$250, $250-$500, $500+ sections
- **Browse by Recipient:** For him, for her, for teens, for kids, for parents, for gamers, for creators
- **Best-Sellers Section:** Top 10-15 most-gifted items with popularity badges
- **Product Cards:** Image, price, recipient suitability, "why it's perfect," gift options
- **Gift Bundles:** Pre-packaged sets with savings
- **Gift Services:** Wrap, message, direct-to-recipient shipping options
- **Tech Level Filters:** Beginner-friendly, intermediate, advanced users
- **Interest Categories:** Gaming, photography, music, fitness, productivity
- **Last-Minute Gifts:** Quick ship, digital options, gift cards
- **Gift Finder Quiz:** Quick questions to recommend perfect gift
- **Occasion-Specific:** Holiday, graduation, birthday sub-guides
- **Corporate Gifting:** Bulk options if applicable
- **Gift Returns Policy:** Easy exchange for recipients
- **Gift Registry:** Wishlist/registry creation option
- **CTAs:** Hero, price sections, recipient categories (numerous)
- **FAQ:** 5-7 questions (Gift wrap cost? Ship to recipient? Message include? Returns? Last-minute options?)

**Goal:** Maximize gift purchases by eliminating decision paralysis through organized price/recipient filtering, best-seller validation, and gift services that simplify the tech gifting experience for both expert and non-technical buyers.`,
  },
];
